import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useBoolean } from 'react-use'
import { closestCenter, DndContext } from '@dnd-kit/core'
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers'
import { arrayMove, rectSwappingStrategy, SortableContext } from '@dnd-kit/sortable'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  DefaultVariableResult,
  VARIABLE_SELECT_FEATURE_FLAG_ALL,
  VariableActionOption,
  VariableSelectFeatureFlag,
} from '@makyoapp/makyo-common'

import { Option } from '@/shared/types'

import { useSortableHandle } from '../../hooks/useSortableHandle'
import VariableChip from './VariableChip/VariableChip'
import { VariableSelectContext } from './VariableSelectContext'
import VariableSelectDropdown from './VariableSelectDropdown'

interface VariableSelectProps {
  id: string
  icon?: React.ReactNode
  featureFlags?: VariableSelectFeatureFlag
  onChange: (value?: DefaultVariableResult | DefaultVariableResult[]) => void
  value?: DefaultVariableResult | DefaultVariableResult[]
  multiple?: boolean
  placeholder?: string
  forceUserSpecific?: boolean
  children?: React.ReactNode
  variableActionOptions?: {
    type: VariableActionOption
  }
  variableColumnOptions?: {
    columnTypes: ColumnTypes[]
    excludeColIds: string[]
  }
  variableCustomTextOptions?: {
    inputType: 'text' | 'number' | 'datetime'
    placeholder?: string
    validation?: (value: string) => void
  }
  limitType?: {
    [key in VariableSelectFeatureFlag]?: number
  }
  tableId?: string
  error?: string
  options?: Option[]
  disableInput?: boolean
  flagOrder?: VariableSelectFeatureFlag[]
}

// TODO (ACTION-WORKFLOWS): REFACTOR THIS
// Refactor how we effectively separate between variable select for normal and extended actions
export function VariableSelectV2({
  id,
  icon,
  featureFlags = VARIABLE_SELECT_FEATURE_FLAG_ALL,
  value,
  multiple = false,
  placeholder,
  onChange,
  forceUserSpecific = false,
  children,
  variableColumnOptions = {
    columnTypes: Object.values(ColumnTypes),
    excludeColIds: [],
  },
  variableActionOptions = {
    type: VariableActionOption.None,
  },
  variableCustomTextOptions = {
    inputType: 'text',
  },
  limitType = {},
  tableId,
  error,
  options,
  disableInput = false,
  flagOrder,
}: VariableSelectProps) {
  const [open, setOpen] = useBoolean(false)
  const [preventClose, setPreventClose] = useBoolean(false)
  const isArr = Array.isArray(value)
  const isUndef = typeof value === 'undefined'

  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!Array.isArray(value) && value?.flag === VariableSelectFeatureFlag.CustomText && !disableInput) {
      inputRef.current?.focus()
    }
  }, [disableInput, value])

  const { sensors, handleDragEnd } = useSortableHandle(
    useCallback(
      (active, over) => {
        if (multiple && isArr && active.id !== over.id) {
          const oldIndex = value.findIndex((v) => v.id === active.id)
          const newIndex = value.findIndex((v) => v.id === over.id)
          const newArr = arrayMove(value, oldIndex, newIndex)
          onChange(newArr)
        }
      },
      [onChange, multiple, isArr, value]
    )
  )

  const renderChips = useCallback(() => {
    if ((!multiple && isUndef) || (multiple && isArr && value.length === 0)) {
      return <div className='text-light'>{placeholder}</div>
    }
    if (multiple && isArr) {
      return value.map((val, i) => <VariableChip key={val.id} index={i} value={val} />)
    }
    if (!multiple && !isUndef && !isArr) {
      return <VariableChip index={0} value={value} />
    }
  }, [isArr, isUndef, multiple, placeholder, value])

  const items = useMemo(() => {
    if (multiple && isArr) {
      return value.map((item) => item.id)
    }
    return []
  }, [multiple, isArr, value])

  return (
    <VariableSelectContext.Provider
      value={{
        id,
        open,
        setOpen,
        preventClose,
        setPreventClose,
        icon,
        onChange,
        value: multiple ? (value === undefined ? [] : value) : value,
        featureFlags,
        multiple,
        forceUserSpecific,
        variableActionOptions,
        variableColumnOptions,
        variableCustomTextOptions,
        inputRef,
        limitType,
        tableId,
        options,
        disableInput,
        flagOrder,
      }}
    >
      <div id={id} className='mk-var-select'>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToFirstScrollableAncestor]}
        >
          <div className='w-full flex-c flex-wrap [&>div]:w-full'>
            <SortableContext items={items} strategy={rectSwappingStrategy}>
              {renderChips()}
            </SortableContext>
          </div>
        </DndContext>
        {(featureFlags as number) !== 0 && <VariableSelectDropdown />}
      </div>
      {error && <div className='mk-input-error'>{error}</div>}
    </VariableSelectContext.Provider>
  )
}
